import { useState, useRef, useEffect } from "react";
import { useProjects } from "@/hooks/useProjects";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import * as Icons from 'lucide-react';
import { ChevronsUpDown, Check, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface ProjectSelectorProps {
  value?: string;
  onValueChange: (value: string | undefined) => void;
  brandId?: string;
  placeholder?: string;
}

export function ProjectSelector({ 
  value, 
  onValueChange, 
  brandId,
  placeholder = "Select a project" 
}: ProjectSelectorProps) {
  const { projects, isLoading } = useProjects(brandId);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedProject = projects.find(p => p.id === value);
  const IconComponent = selectedProject?.icon 
    ? (Icons as any)[selectedProject.icon] 
    : null;

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        disabled={isLoading}
        className={cn(
          "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background",
          "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
          "disabled:cursor-not-allowed disabled:opacity-50"
        )}
      >
        {isLoading ? (
          <div className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Loading...</span>
          </div>
        ) : selectedProject ? (
          <div className="flex items-center gap-2">
            {IconComponent && <IconComponent className="h-4 w-4" />}
            <span>{selectedProject.name}</span>
          </div>
        ) : (
          <span className="text-muted-foreground">{placeholder}</span>
        )}
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </button>

      {open && (
        <div className="absolute top-full left-0 right-0 z-[9999] mt-1 rounded-md border bg-popover shadow-md">
          <Command>
            <CommandInput placeholder="Search projects..." autoFocus />
            <CommandList className="max-h-[200px]">
              <CommandEmpty>No projects found.</CommandEmpty>
              <CommandGroup>
                <CommandItem
                  value="__none__"
                  onSelect={() => {
                    onValueChange(undefined);
                    setOpen(false);
                  }}
                >
                  <Check className={cn("mr-2 h-4 w-4", !value ? "opacity-100" : "opacity-0")} />
                  <span className="text-muted-foreground">No Project</span>
                </CommandItem>
                {projects.map((project) => {
                  const ProjectIcon = project.icon 
                    ? (Icons as any)[project.icon] 
                    : Icons.Folder;
                  
                  return (
                    <CommandItem
                      key={project.id}
                      value={project.name}
                      onSelect={() => {
                        onValueChange(project.id);
                        setOpen(false);
                      }}
                    >
                      <Check className={cn("mr-2 h-4 w-4", value === project.id ? "opacity-100" : "opacity-0")} />
                      <ProjectIcon className="h-4 w-4 mr-2" />
                      <span>{project.name}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </div>
      )}
    </div>
  );
}
